import { toastSuccess } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"

import { useStatefulFieldRemount } from "Catalog/shared/components/StatefulFieldRemountContext"
import { useUploadCatalogMutation } from "shared/hooks/mutations"
import useEditor from "shared/hooks/useEditor"
import useNavigateNoUpdate from "shared/hooks/useNavigateNoUpdate"

import Reload from "./Reload"
import { StyledToolbar, CommandGroup, SaveGroup, ToolButton } from "./Styles"

interface CatalogToolbarProps {
  commandActions?: React.ReactNode
}

const CatalogToolbar = ({ commandActions }: CatalogToolbarProps): JSX.Element => {
  const { editor, catalog, onChange } = useEditor()
  const { remount } = useStatefulFieldRemount()
  const navigate = useNavigateNoUpdate()
  const { t } = useTranslation()

  const [error, setError] = React.useState<string | undefined>(undefined)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)

  const mutation = useUploadCatalogMutation({
    onSuccess: (catalog) => {
      editor.catalogSaved(catalog)
      setIsSaving(false)
      toastSuccess(t("catalog.upload.success"))
      onChange()
      remount()
    },
  })
  if (mutation.error && error === undefined) setError(JSON.stringify(mutation.error))

  const handleStepResult = (path: string | undefined): void => {
    if (path !== undefined) navigate(path)
    remount()
  }

  const handleSave = (): void => {
    setIsSaving(true)
    mutation.mutate(catalog)
  }

  const saveActions = (
    <>
      <ToolButton
        icon="undo"
        variant="emptyOverGray"
        onClick={() => handleStepResult(editor.undo())}
        disabled={!editor.canUndo}
      >
        {t("toolbar.undo")}
      </ToolButton>

      <ToolButton
        icon="redo"
        variant="emptyOverGray"
        onClick={() => handleStepResult(editor.redo())}
        disabled={!editor.canRedo}
      >
        {t("toolbar.redo")}
      </ToolButton>

      <Reload />

      <ToolButton variant="primary" onClick={handleSave} disabled={!editor.unsaved} isWorking={isSaving}>
        {t("toolbar.save")}
      </ToolButton>

      {error !== undefined && <a onClick={() => alert(error)}>Save error!</a>}
    </>
  )

  return (
    <StyledToolbar data-testid="toolbar">
      {commandActions !== undefined && <CommandGroup>{commandActions}</CommandGroup>}
      <SaveGroup>{saveActions}</SaveGroup>
    </StyledToolbar>
  )
}

export default CatalogToolbar

export { ToolButton }
